/**
 * Chrome-i i përbashkët i formularëve të aseteve.
 *
 * Ekziston për të njëjtën arsye si `ReportKit`: dendësia dhe ritmi i një
 * formulari nuk guxojnë të jetojnë faqe-më-faqe. Regjistrimi i asetit dhe
 * procesverbali kanë të njëjtat blloqe — kokë me ikonë, rrjet fushash, tabelë
 * rreshtash — dhe kur secili i vendos vetë hapësirat, dy ekrane të të njëjtit
 * modul nisin të duken si dy produkte.
 *
 * ── Ç'vendos ky skedar ──────────────────────────────────────────────────────
 *
 * **Një ritëm etiketash.** Çdo fushë e mban etiketën SIPËR (`shrink`), gjithmonë.
 * Përzierja e etiketave brenda fushës (kur është bosh) me ato sipër (kur ka
 * vlerë) e bënte rreshtin të kërcente gjatë shkrimit dhe linte fushat e një
 * rreshti me bazamente të ndryshme. Vendi i shpjegimit është tani placeholder-i,
 * jo `helperText`: një rresht ndihmës nën vetëm dy fusha nga katër e prish
 * rrjetin vertikal të tërë rreshtit.
 *
 * **Njësitë shkruhen hapur.** Te `sx`, `padding: 12` do të thotë 12 × 8px = 96px.
 * Ky gabim preku 18 raporte njëherësh (shih `ui_conventions.md`), ndaj çdo vlerë
 * brenda një përzgjedhësi të mbivendosur këtu ka `'px'`.
 */

import React from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';

/**
 * Dendësia e formularëve. Vihet NJË herë mbi kontejnerin e faqes; fushat brenda
 * nuk e përsërisin. Tema jep 16.5px padding vertikal për `size="small"` — mbi
 * një faqe me tridhjetë fusha ajo është një ekran i tërë skroll.
 */
export const FORM_DENSITY = {
  '& .MuiOutlinedInput-input':        { padding: '8.5px 11px', fontSize: '0.8125rem' },
  '& .MuiInputBase-inputMultiline':   { padding: '0' },
  '& .MuiOutlinedInput-multiline, & .MuiInputBase-multiline': { padding: '8.5px 11px' },
  '& .MuiInputLabel-root':            { fontSize: '0.78rem' },
  '& .MuiFormHelperText-root':        { marginTop: '2px', marginLeft: '2px', fontSize: '0.66rem' },
  '& .MuiSelect-select':              { paddingTop: '8.5px', paddingBottom: '8.5px' },
  // Autocomplete e vë vetë padding-un e vet mbi rrënjën; pa këtë rri 6px më i lartë
  // se fushat pranë tij, dhe rreshti humbet bazamentin e përbashkët.
  '& .MuiAutocomplete-inputRoot':     { paddingTop: '2px', paddingBottom: '2px' },
  // Tabelat brenda formularit (specifikimet, aksesorët) mbajnë inpute — pa këtë
  // secili rresht bëhet 62px dhe pesë aksesorë hanë gjysmë ekrani.
  '& .MuiTableHead-root .MuiTableCell-root': {
    padding: '6px 10px', fontSize: '0.62rem', whiteSpace: 'nowrap',
  },
  '& .MuiTableBody-root .MuiTableCell-root': { padding: '4px 10px', border: 0 },
};

/**
 * Bllok i formularit. Koka është një rresht 24px: ikonë, titull, veprim —
 * pa nëntitull, sepse titulli i një blloku formulari nuk ka çka shpjegon.
 */
export function Section({ title, icon, action, children, sx }) {
  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, p: 1.5, minWidth: 0, ...sx }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between"
        sx={{ mb: 1.25, minHeight: 26 }}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          {icon}
          <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.01em' }}>
            {title}
          </Typography>
        </Stack>
        {action}
      </Stack>
      {children}
    </Paper>
  );
}

/**
 * Vlerë e llogaritur, jo fushë.
 *
 * "Vlera e mbetur" ishte një `TextField` i çaktivizuar: dukej si vend ku shkruhet
 * diçka, dhe përdoruesi provonte të shkruante. Një shifër që e nxjerr sistemi
 * duhet të duket si shifër.
 */
export function ReadOut({ label, value, hint }) {
  return (
    <Box sx={{
      px: 1.25, py: 0.75, borderRadius: 1.5, minWidth: 0,
      border: '1px dashed', borderColor: 'divider', bgcolor: 'action.hover',
    }}>
      <Typography noWrap sx={{
        fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.05em',
        textTransform: 'uppercase', color: 'text.secondary',
      }}>
        {label}
      </Typography>
      <Typography noWrap sx={{ fontSize: '0.85rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
      {hint && (
        <Typography noWrap sx={{ fontSize: '0.62rem', color: 'text.disabled' }}>{hint}</Typography>
      )}
    </Box>
  );
}

/** Props-et që e mbajnë etiketën sipër te ÇDO fushë. */
export const SHRINK = { InputLabelProps: { shrink: true } };
